///ClauseEditor.jsx

import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Modal, Form, Input, Select, Button, Alert, message, Spin, Switch } from 'antd';
import { fetchTargetFieldValues, updateClause, testClause, addClause } from '../functions/alertClauseService';
import TextCrafter from './TextCrafter';
import AlertTestingResultsTable from './AlertTestingResultsTable';

const { Option } = Select;

const ClauseEditor = ({ visible, onClose, clause, dashboardStructure, refreshClauses, isFromTweak = false, isFromProposal = false, alertId = null, onClauseReplaced = null, onProposalClauseSaved = null }) => {
  const [editingClause, setEditingClause] = useState(clause);
  const [targetFieldValues, setTargetFieldValues] = useState([]);
  const [thresholdTargetFieldValues, setThresholdTargetFieldValues] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);  // Tracks if there are changes
  const operatorOptions = ['>', '<', '=', '>=', '<=', '!='];
  const [showWarning, setShowWarning] = useState(false);
  const [isTextCrafterVisible, setTextCrafterVisible] = useState(false); 
  const parametersData = useSelector((state) => state.portfolio?.parametersData?.DP_dataset_json || []);
  const [testResultMessage, setTestResultMessage] = useState(null);
  const [testResults, setTestResults] = useState([]); // Store test results for modal display
  const [isTestResultsModalVisible, setIsTestResultsModalVisible] = useState(false); // Control test results modal visibility
  const [isTesting, setIsTesting] = useState(false); // Add state for spinner
  
  // ✨ NEW: State for Save and Replace functionality (DAS-1209)
  const [isClauseSaved, setIsClauseSaved] = useState(false); // Track if clause has been saved
  const [originalClauseId, setOriginalClauseId] = useState(null); // Store original clause ID for replacement

  // ✨ NEW: Initialize hasChanges when isFromProposal changes (DAS-1324)
  useEffect(() => {
    if (isFromProposal) {
      setHasChanges(true); // Enable save button immediately for proposals with pre-filled data
    }
  }, [isFromProposal]);

  // ✨ NEW: Initialize original clause ID when coming from tweak (DAS-1209)
  useEffect(() => {
    if (isFromTweak && clause && clause.original_clause_id) {
      setOriginalClauseId(clause.original_clause_id);
    }
  }, [isFromTweak, clause]);

  // console.log("dashboard structure in clause editor", dashboardStructure)
  const tableNames = useMemo(() => (dashboardStructure ? Object.keys(dashboardStructure) : []), [dashboardStructure]);
  const getThresholdOptions = () => {
    if (!parametersData || !Array.isArray(parametersData)) {
      console.log("parametersData is not available or is not an array.");
      return null;
    }
    
    if (editingClause.clause_field && editingClause.clause_field_sourcetable) {
      const matchedParam = parametersData.find(
        (param) =>
          param.SourceTable === editingClause.clause_field_sourcetable &&
          param.DataField === editingClause.clause_field
      );
  
      // console.log("Matched Param:", matchedParam); // Log to confirm match
      if (matchedParam && matchedParam.Values) {
        // Return options from matchedParam.Values, splitting by commas
        return matchedParam.Values.split(',').map((value) => value.trim());
      }
    }
    
    return null; // Return null if no matching parameter is found
  };
  
 


  const handleTestClause = async () => {
    setIsTesting(true); // Show spinner
    const isMandatoryFilled = editingClause?.clause_field && editingClause?.clause_operator && editingClause?.clause_txt;
    const isScenario1 = editingClause?.threshold;
    const isScenario2 = editingClause?.threshold_field && editingClause?.threshold_sourcetable;
  
    if (!isMandatoryFilled || (!isScenario1 && !isScenario2)) {
      message.warning("Form is not complete for testing. Please fill out required fields.");
      setIsTesting(false); // Hide spinner
      return;
    }
  
    try {
      const result = await testClause(editingClause);
      setTestResults(result); // Store the full test results for the modal
      setTestResultMessage(`${result.length} True`); // Set test result message to display number of true results
      message.info(`${result.length} True`); // Show message with number of true results
    } catch (error) {
      console.error('Error testing clause:', error);
      setTestResultMessage('Error testing clause.');
    } finally {
      setIsTesting(false); // Hide spinner
    }
  };
  

  // console.log("clause in ClauseEditor", clause)

  useEffect(() => {
    setEditingClause(clause);
    // If opened from a tweak, treat prefilled suggestion as a pending change so Save is enabled
    setHasChanges(!!isFromTweak);
  }, [clause, isFromTweak]);

  useEffect(() => {
    if (editingClause?.clause_field_target && editingClause?.clause_field_sourcetable) {
      const loadTargetFieldValues = async () => {
        try {
          const values = await fetchTargetFieldValues(editingClause.clause_field_sourcetable, editingClause.clause_field_target);
          setTargetFieldValues(values);
        } catch (error) {
          console.error('Error fetching target field values:', error);
        }
      };
      loadTargetFieldValues();
    }
  }, [editingClause?.clause_field_target, editingClause?.clause_field_sourcetable]);

  useEffect(() => {
    if (editingClause?.threshold_field_target && editingClause?.threshold_sourcetable) {
      const loadThresholdTargetFieldValues = async () => {
        try {
          const values = await fetchTargetFieldValues(editingClause.threshold_sourcetable, editingClause.threshold_field_target);
          setThresholdTargetFieldValues(values);
        } catch (error) {
          console.error('Error fetching threshold target field values:', error);
        }
      };
      loadThresholdTargetFieldValues();
    }
  }, [editingClause?.threshold_field_target, editingClause?.threshold_sourcetable]);

  // Update handleFieldChange to trigger the warning condition
  const handleFieldChange = (field, value, option = {}) => {
    setEditingClause((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true); // Mark as changed
  
    if (field === "clause_field_sourcetable") {
      const selectedTableFields = dashboardStructure[value];
  
      if (value === "Portfolio_View" || value === "Fundamental_data_trends_per_ticker" || value === "Portfolio_Exposure" || value === "ticker") {
      // if (value === "Portfolio_View") {
        // Autofill with 'ticker' for 'Portfolio_View'
        setEditingClause((prev) => ({
          ...prev,
          clause_field_target: "ticker",
          clause_field_target_value: "ticker",
        }));
      } else {
        const hasTickerField = selectedTableFields?.some((f) => f.name === "ticker");
        const endsWithData = value.toLowerCase().endsWith("_data");
  
        if (hasTickerField && endsWithData) {
          setEditingClause((prev) => ({
            ...prev,
            clause_field_target: "ticker",
            clause_field_target_value: "ticker",
          }));
        } else {
          setEditingClause((prev) => ({
            ...prev,
            clause_field_target: null,
            clause_field_target_value: null,
          }));
        }
      }
    }
  
    if (field === "threshold_field") {
      // DAS-2265: Only update threshold_sourcetable if option.table is explicitly provided.
      // When called from the threshold_sourcetable onChange (to reset the field to null),
      // option is {} and option.table is undefined — we must NOT wipe the sourcetable.
      setEditingClause((prev) => ({
        ...prev,
        threshold_field: value,
        ...(option.table ? { threshold_sourcetable: option.table } : {}),
      }));
    }
  
    if (field === "threshold_sourcetable" && value === "Category_Userstatus") {
      setEditingClause((prev) => ({
        ...prev,
        threshold_field_target: "depending on userid/category",
        threshold_field_target_value: "depending on userid/category",
      }));
    }

    if (field === "threshold_sourcetable" && value === "Sector_Benchmarks") {
      setEditingClause((prev) => ({
        ...prev,
        threshold_field_target: "sector",
        threshold_field_target_value: "sector",
      }));
    }

    if (field === "threshold_sourcetable" && value === "Portfolio_View") {
      setEditingClause((prev) => ({
        ...prev,
        threshold_field_target: "ticker",
        threshold_field_target_value: "ticker",
      }));
    }

    if (field === "threshold_sourcetable" && value === "UserFavorites") {
      setEditingClause((prev) => ({
        ...prev,
        threshold_field_target: "Ticker",
        threshold_field_target_value: "Ticker",
      }));
    }

    if (field === "threshold_sourcetable" && value === "YF_Data") {
      setEditingClause((prev) => ({
        ...prev,
        threshold_field_target: "ticker",
        threshold_field_target_value: "ticker",
      }));
    }

  
    if (field === "threshold" && value) {
      const { threshold_field, threshold_field_target, threshold_field_target_value } = editingClause;
      setShowWarning(!!(threshold_field || threshold_field_target || threshold_field_target_value));
    } else if (
      field === "threshold_field" ||
      field === "threshold_field_target" ||
      field === "threshold_field_target_value"
    ) {
      setShowWarning(!!editingClause.threshold);
    } else {
      setShowWarning(false);
    }
  };
  

  const [canTest, setCanTest] = useState(true);

useEffect(() => {
    setCanTest(isFormValid()); // Update testability whenever the form changes
}, [editingClause]);

  
const handleSaveClause = async () => {
  try {
      let savedClause;
      if (editingClause.ID) {
          // Update existing clause
          savedClause = await updateClause(editingClause.ID, editingClause);
          message.success("Clause updated successfully");
      } else {
          // Add new clause
          savedClause = await addClause(editingClause);
          message.success("New clause added successfully");
      }

      // Update the form with the fully returned saved clause
      setEditingClause(savedClause);

      setHasChanges(false); // Reset the hasChanges state
      refreshClauses(savedClause); // Refresh the clause list in the parent component
      
      // ✨ NEW: Mark clause as saved for Save and Replace functionality (DAS-1209)
      if (isFromTweak) {
        setIsClauseSaved(true);
        message.success("Clause saved! You can now use 'Save and Replace' to replace the original clause.");
      }
      
      // ✨ NEW: Mark clause as saved for proposals (show button to add to alert)
      if (isFromProposal) {
        setIsClauseSaved(true);
        message.success("Clause saved! You can now add it to the alert.");
      }
  } catch (error) {
      console.error('Error saving clause:', error);
      message.error("Failed to save the clause.");
  }
};

// ✨ NEW: Handle Add Proposal Clause to Alert functionality
const handleAddProposalClauseToAlert = async () => {
  if (!isClauseSaved || !editingClause.ID || !alertId) {
    message.error("Cannot add clause to alert: missing required data");
    return;
  }

  // Determine if this should be a KO or AFF clause based on the clause's invisible property
  const clauseType = editingClause.invisible ? 'KO' : 'AFF';
  
  // Show confirmation dialog
  Modal.confirm({
    title: `Add as ${clauseType} Clause to Alert`,
    content: (
      <div>
        <p>Are you sure you want to add this clause as a {clauseType} clause to Alert {alertId}?</p>
        <div style={{ 
          marginTop: '12px', 
          padding: '8px', 
          backgroundColor: '#f5f5f5', 
          borderRadius: '4px',
          fontSize: '12px'
        }}>
          <strong>Clause ID:</strong> {editingClause.ID}<br/>
          <strong>Clause Text:</strong> {editingClause.clause_txt}<br/>
          <strong>Type:</strong> {clauseType}
        </div>
      </div>
    ),
    okText: `Yes, Add as ${clauseType}`,
    cancelText: 'Cancel',
    okType: 'primary',
    onOk: async () => {
      try {
        // Call the parent callback to add the clause to the alert
        if (onProposalClauseSaved) {
          onProposalClauseSaved(editingClause.ID, clauseType);
        }
      } catch (error) {
        console.error('Error adding clause to alert:', error);
        message.error("Failed to add clause to alert.");
      }
    },
    onCancel() {
      console.log('Add clause to alert cancelled by user');
    },
  });
};

// ✨ NEW: Handle Save and Replace functionality (DAS-1209)
const handleSaveAndReplace = async () => {
  if (!isClauseSaved || !editingClause.ID || !originalClauseId || !alertId) {
    message.error("Cannot perform Save and Replace: missing required data");
    return;
  }

  // Show confirmation dialog
  Modal.confirm({
    title: 'Save and Replace Clause',
    content: (
      <div>
        <p>Are you sure you want to replace the original clause with this new clause?</p>
        <div style={{ 
          marginTop: '12px', 
          padding: '8px', 
          backgroundColor: '#f5f5f5', 
          borderRadius: '4px',
          fontSize: '12px'
        }}>
          <strong>Alert ID:</strong> {alertId}<br/>
          <strong>Original Clause ID:</strong> {originalClauseId}<br/>
          <strong>New Clause ID:</strong> {editingClause.ID}<br/>
          <strong>New Clause Text:</strong> {editingClause.clause_txt}
        </div>
      </div>
    ),
    okText: 'Yes, Replace',
    cancelText: 'Cancel',
    okType: 'danger',
    onOk: async () => {
      try {
        // Call the backend to replace the clause
        const response = await fetch('/api/replace_affirmative_clause', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            alert_id: alertId,
            old_clause_id: originalClauseId,
            new_clause_id: editingClause.ID
          })
        });

        if (response.ok) {
          message.success(`Clause ${originalClauseId} replaced with clause ${editingClause.ID} in Alert ${alertId}`);
          
          // ✨ NEW: Call the callback to notify parent component (DAS-1209)
          if (onClauseReplaced) {
            onClauseReplaced(originalClauseId, editingClause.ID);
          } else {
            // Fallback: Close the modal
            onClose();
          }
        } else {
          const errorData = await response.json();
          message.error(`Failed to replace clause: ${errorData.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error replacing clause:', error);
        message.error("Failed to replace clause.");
      }
    },
    onCancel() {
      console.log('Save and Replace cancelled by user');
    },
  });
};

  



  const getFieldOptions = (table, excludeField = null) => {
    const options = [];
    if (dashboardStructure && table) {

      dashboardStructure[table]?.forEach(field => {
        if (field.name !== excludeField) {
          options.push({
            value: field.name,
            label: field.name,
          });
        }
      });
    }
    return options;
  };

  // Form validation: Ensure required fields are not empty
  const isFormValid = () => {
    const clauseRelatedFieldsPopulated = !!(editingClause?.clause_field && editingClause?.clause_operator);
    
    // Scenario 1: If Threshold is chosen, skip other threshold-related fields
    const scenario1 = !!(editingClause?.threshold && clauseRelatedFieldsPopulated);
  
    // Scenario 2: If Threshold Field and Sourcetable are filled but Threshold is empty
    const scenario2 = !!(editingClause?.threshold_field && editingClause?.threshold_sourcetable && clauseRelatedFieldsPopulated);
  
    // Original Condition: All required fields are filled
    const allFieldsFilled = !!(clauseRelatedFieldsPopulated &&
      editingClause?.threshold_field &&
      editingClause?.threshold_sourcetable &&
      editingClause?.threshold_field_target &&
      editingClause?.threshold_field_target_value);
  
    return scenario1 || scenario2 || allFieldsFilled;
  };
  

  return (
    <>
    <Modal
      title={
        isFromTweak 
          ? `Create New Clause (for Tweaking Alert ${alertId})`
          : isFromProposal
            ? `Create New Clause (from Proposal for Alert ${alertId})`
            : editingClause?.ID 
              ? `Edit Clause ${editingClause.ID}` 
              : "Create New Clause"
      }
      //  title={`Edit Clause ${editingClause?.ID || ''}`}
      // width={1800}
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,

<Button
key="test"
onClick={handleTestClause}
disabled={!canTest} // Use the new state for the Test button
>
{isTesting ? <Spin /> : 'Test'}
</Button>,

          <Button
          key="save"
          type="primary"
          onClick={handleSaveClause}
          disabled={(() => {
            const formValid = isFormValid();
            // DAS-1324: For proposals, enable save button if form is valid (regardless of hasChanges)
            const shouldEnableForProposal = isFromProposal && formValid;
            return !shouldEnableForProposal && (!hasChanges || !formValid);
          })()}
          >
          {editingClause?.ID ? "Update" : "Save"}
          </Button>,
          
          // ✨ NEW: Save and Replace button (DAS-1209)
          ...(isFromTweak && isClauseSaved && editingClause?.ID ? [
            <Button
              key="saveAndReplace"
              type="primary"
              danger
              onClick={handleSaveAndReplace}
            >
              Save and Replace
            </Button>
          ] : []),
          
          // ✨ NEW: Add Proposal Clause to Alert button
          ...(isFromProposal && isClauseSaved && editingClause?.ID ? [
            <Button
              key="addProposalClause"
              type="primary"
              onClick={handleAddProposalClauseToAlert}
            >
              Add as {editingClause.invisible ? 'KO' : 'AFF'} clause to alert {alertId}
            </Button>
          ] : [])
      ]}
      width={1000}
    >

{/* Display the link with test results count if available */}
{testResultMessage && (
          <div style={{ marginBottom: '16px' }}>
            <a onClick={() => setIsTestResultsModalVisible(true)}>{testResultMessage}</a>
          </div>
        )}


      <Form layout="vertical">
        {/* Schema loading indicator */}
        {!dashboardStructure ? (
          <div style={{ textAlign: 'center', padding: 12 }}>
            <Spin />
            <div>Loading schema…</div>
          </div>
        ) : null}

        {/* Clause Field selection split: first choose table, then field */}
        <Form.Item label="Clause Field Sourcetable" required>
          <Select
            showSearch
            value={editingClause.clause_field_sourcetable || undefined}
            onChange={(value) => {
              handleFieldChange("clause_field_sourcetable", value);
              // Reset dependent field when table changes
              handleFieldChange("clause_field", null);
            }}
            placeholder="Select table"
            allowClear
            virtual
            dropdownMatchSelectWidth={false}
            optionFilterProp="children"
          >
            {tableNames.map((table) => (
              <Option key={table} value={table}>
                {table}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Clause Field" required>
          <Select
            showSearch
            value={editingClause.clause_field || undefined}
            onChange={(value) => handleFieldChange("clause_field", value)}
            placeholder={editingClause.clause_field_sourcetable ? "Select field" : "Select a table first"}
            disabled={!editingClause.clause_field_sourcetable}
            allowClear
            virtual
            dropdownMatchSelectWidth={false}
            optionFilterProp="children"
          >
            {getFieldOptions(editingClause.clause_field_sourcetable)?.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Clause Field Target">
          <Select
            showSearch
            value={editingClause.clause_field_target}
            onChange={value => handleFieldChange('clause_field_target', value)}
          >
            {dashboardStructure && getFieldOptions(editingClause.clause_field_sourcetable, editingClause.clause_field).map((option) => (
            // {getFieldOptions(editingClause.clause_field_sourcetable, editingClause.clause_field).map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Clause Field Target Value">
          <Select
            showSearch
            value={editingClause.clause_field_target_value}
            onChange={value => handleFieldChange('clause_field_target_value', value)}
          >
            {targetFieldValues.map(value => (
              <Option key={value} value={value}>
                {value}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Operator" required>
          <Select
            value={editingClause.clause_operator}
            onChange={value => handleFieldChange('clause_operator', value)}
          >
            {operatorOptions.map(op => (
              <Option key={op} value={op}>
                {op}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Multi-Threshold">
          <Switch
            checked={editingClause.multi_threshold || false}
            onChange={(checked) => handleFieldChange('multi_threshold', checked)}
            checkedChildren="Yes"
            unCheckedChildren="No"
          />
          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            Enable to use multiple threshold values (comma-separated)
          </div>
        </Form.Item>

        <Form.Item label="Threshold">
  {getThresholdOptions() ? (
    <Select
      showSearch
      allowClear // Enables the 'x' to clear selection
      value={editingClause.threshold}
      onChange={(value) => handleFieldChange('threshold', value)}
      placeholder={editingClause.multi_threshold ? "Select multiple values (comma-separated)" : "Select or clear threshold value"}
      mode={editingClause.multi_threshold ? "tags" : undefined}
    >
      {getThresholdOptions().map((option) => (
        <Option key={option} value={option}>
          {option}
        </Option>
      ))}
    </Select>
  ) : (
    <Input
      allowClear // Enables the 'x' to clear input
      value={editingClause.threshold}
      onChange={(e) => handleFieldChange('threshold', e.target.value)}
      placeholder={editingClause.multi_threshold ? "Enter multiple values (comma-separated)" : "Enter or clear threshold value"}
    />
  )}
</Form.Item>



        {/* Threshold Field selection split: first choose table, then field */}
        <Form.Item label="Threshold Sourcetable" required>
          <Select
            showSearch
            value={editingClause.threshold_sourcetable || undefined}
            onChange={(value) => {
              handleFieldChange("threshold_sourcetable", value);
              // Reset dependent field when table changes
              handleFieldChange("threshold_field", null);
            }}
            placeholder="Select table"
            allowClear
            virtual
            dropdownMatchSelectWidth={false}
            optionFilterProp="children"
          >
            {tableNames.map((table) => (
              <Option key={table} value={table}>
                {table}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Threshold Field" required>
          <Select
            showSearch
            value={editingClause.threshold_field || undefined}
            onChange={(value) => handleFieldChange("threshold_field", value)}
            placeholder={editingClause.threshold_sourcetable ? "Select field" : "Select a table first"}
            disabled={!editingClause.threshold_sourcetable}
            allowClear
            virtual
            dropdownMatchSelectWidth={false}
            optionFilterProp="children"
          >
            {getFieldOptions(editingClause.threshold_sourcetable)?.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>




        {/* Threshold Sourcetable now handled above */}

        <Form.Item label="Threshold Field Target">
          <Select
            showSearch
            value={editingClause.threshold_field_target}
            onChange={value => handleFieldChange('threshold_field_target', value)}
          >
            {getFieldOptions(editingClause.threshold_sourcetable, editingClause.threshold_field).map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Threshold Field Target Value">
          <Select
            showSearch
            value={editingClause.threshold_field_target_value}
            onChange={value => handleFieldChange('threshold_field_target_value', value)}
          >
            {thresholdTargetFieldValues.map(value => (
              <Option key={value} value={value}>
                {value}
              </Option>
            ))}
          </Select>
        </Form.Item>  

        <Form.Item label="Clause Text">
            <div style={{ border: '1px solid #d9d9d9', padding: '8px', borderRadius: '4px', maxHeight: '100px', overflowY: 'auto', wordWrap: 'break-word' }}>
              <Button
                style={{ whiteSpace: 'normal', textAlign: 'left', padding: '4px' }} // Adjust button styling to wrap text within the box
                onClick={() => {
                  console.log("Opening TextCrafter modal...");
                  setTextCrafterVisible(true);
                }}
              >
                Edit Clause Text: {editingClause?.clause_txt}
              </Button>
            </div>
          </Form.Item>

          <Form.Item label="Invisible">
  <Select
    value={editingClause.invisible ? 'true' : 'false'}
    onChange={(val) => handleFieldChange('invisible', val === 'true')}
    style={{ width: 120 }}
  >
    <Option value="false">OFF</Option>
    <Option value="true">ON</Option>
  </Select>
</Form.Item>


      </Form>

      {testResultMessage && (
          <div style={{ margin: '16px 0', padding: '8px', border: '1px solid #d9d9d9', borderRadius: '4px', backgroundColor: '#f6f6f6' }}>
            {testResultMessage}
          </div>
        )}
      {showWarning && (
          <Alert message="If Threshold has a value, the clause will use that as the threshold value!" type="warning" showIcon />
        )}

    </Modal>
    
    {/* Put the TextCrafter modal here */}
    {isTextCrafterVisible && (
        <TextCrafter
          dashboardStructure={dashboardStructure}
          initialText={editingClause?.clause_txt} // Pass clause text as initialText
          onSave={(updatedText) => {
            console.log("Updated Text:", updatedText);
            // Update the editingClause with the new text
            setEditingClause(prev => ({ ...prev, clause_txt: updatedText }));
            setHasChanges(true); // Mark that changes have been made
            setTextCrafterVisible(false); // Close modal
          }}
          onClose={() => {
            // console.log("Closing TextCrafter modal...");
            setTextCrafterVisible(false);
          }}
        />
      )}

      {/* AlertTestingResultsTable modal */}
      <AlertTestingResultsTable
        visible={isTestResultsModalVisible}
        onClose={() => setIsTestResultsModalVisible(false)}
        testResults={testResults}
        columnNames={['Clause text', 'Ticker', 'User']}
      />

      </>
        
      );
  
};

export default ClauseEditor;
